import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, Loader } from 'lucide-react';
import { IconAffiliate } from '@tabler/icons-react';
import './OpenColumnCollapse.css';
import './OpenColumnModern.css';

function OpenColumnCount({ orderCount = 0, customersCount = 0, layout = 'inline' }) {
  const safeCustomers = Math.max(0, Number(customersCount) || 0);
  const safeTotal = Math.max(0, Number(orderCount) || 0);
  const showSplit = safeCustomers > 0;
  const isStack = layout === 'stack';

  return (
    <span
      className={[
        'open-column-count',
        showSplit ? 'open-column-count--split' : '',
        isStack ? 'open-column-count--stack' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={
        showSplit
          ? `${safeTotal} ordens, ${safeCustomers} chamados Customers`
          : `${safeTotal} ordens`
      }
    >
      <span className="open-column-count-main">{safeTotal}</span>
      {showSplit ? (
        <>
          {!isStack ? <span className="open-column-count-sep" aria-hidden="true" /> : null}
          <span className="open-column-count-customers board-clickup-icon-btn-anchor">
            <IconAffiliate
              className="open-column-count-customers-icon"
              size={isStack ? 12 : 11}
              stroke={1.75}
              aria-hidden
            />
            <span className="open-column-count-customers-num">{safeCustomers}</span>
            <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
              Portal Customers
            </span>
          </span>
        </>
      ) : null}
    </span>
  );
}

/**
 * Coluna fixa "Em aberto" com recolher/expandir (estilo ClickUp).
 */
export function OpenColumnBoard({
  orderCount = 0,
  customersCount = 0,
  isRefreshing = false,
  isDragOver = false,
  hasStatusColumn = false,
  bodyScrollRef,
  collapsed: collapsedProp,
  onCollapsedChange,
  onDrop,
  onDragOver,
  onDragLeave,
  onDragEnd,
  children,
}) {
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(false);
  const isControlled = typeof collapsedProp === 'boolean';
  const collapsed = isControlled ? collapsedProp : uncontrolledCollapsed;

  const setCollapsed = useCallback((next) => {
    if (!isControlled) setUncontrolledCollapsed(next);
    onCollapsedChange?.(next);
  }, [isControlled, onCollapsedChange]);

  const handleCollapse = useCallback((e) => {
    e.stopPropagation();
    setCollapsed(true);
  }, [setCollapsed]);

  const handleExpand = useCallback(() => {
    setCollapsed(false);
  }, [setCollapsed]);

  const handleCollapsedDragOver = useCallback(
    (e) => {
      const types = Array.from(e.dataTransfer.types || []);
      const isColumnReorder = types.includes('columnIndex') && types.includes('dragType');
      if (isColumnReorder) return;
      if (types.includes('fromTechnician') || types.includes('orderId')) {
        e.preventDefault();
        setCollapsed(false);
        onDragOver?.(e);
      }
    },
    [onDragOver]
  );

  // Evita virtualizer Em aberto observar nó detachado ao sair do Board
  useEffect(() => {
    return () => {
      if (bodyScrollRef && 'current' in bodyScrollRef) {
        bodyScrollRef.current = null;
      }
    };
  }, [bodyScrollRef]);

  const wrapClass = [
    'kanban-fixed-column',
    'open-column-collapse-wrap',
    hasStatusColumn ? 'has-status-column' : '',
    collapsed ? 'is-collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClass}>
      <div className="open-column-expanded" aria-hidden={collapsed}>
        <div
          className={`kanban-column open-column ${isDragOver ? 'drop-target' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDragEnd={onDragEnd}
        >
          <div className="column-content open-column-content">
            <div className="open-column-header">
              <div className="open-column-title-row">
                <span className="open-column-badge">
                  <Loader className="open-column-badge-icon" size={12} strokeWidth={2} aria-hidden="true" />
                  Em aberto
                </span>
                {isRefreshing ? (
                  <div className="column-loading-indicator">
                    <div className="loading-spinner" />
                  </div>
                ) : null}
                <OpenColumnCount orderCount={orderCount} customersCount={customersCount} />
              </div>
              <div className="open-column-header-actions">
                <button
                  type="button"
                  className="open-column-collapse-btn board-clickup-icon-btn-anchor"
                  aria-label="Recolher coluna"
                  onClick={handleCollapse}
                >
                  <ChevronLeft size={15.4} strokeWidth={2.2} aria-hidden />
                  <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
                    Recolher coluna
                  </span>
                </button>
              </div>
            </div>
            <div className="open-column-body custom-scroll" ref={bodyScrollRef}>
              {children}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="open-column-collapsed"
        aria-label={`Expandir coluna Em aberto (${orderCount} ordens)`}
        aria-hidden={!collapsed}
        tabIndex={collapsed ? 0 : -1}
        onClick={handleExpand}
        onDragOver={handleCollapsedDragOver}
      >
        <span className="open-column-collapsed-inner">
          <span className="open-column-collapsed-badge">
            <Loader className="open-column-collapsed-badge-icon" size={12} strokeWidth={2} aria-hidden="true" />
            <span className="open-column-collapsed-label">EM ABERTO</span>
          </span>
          <OpenColumnCount
            orderCount={orderCount}
            customersCount={customersCount}
            layout="stack"
          />
        </span>
      </button>
    </div>
  );
}
